import { Icon } from "@platform/ui/components";
import { Slot, Slottable } from "@radix-ui/react-slot";
import * as React from "react";
import { tv, VariantProps } from "tailwind-variants";

interface BannerProps extends React.ComponentPropsWithoutRef<"div">, VariantProps<typeof bannerStyles> {}
export const Banner = React.forwardRef<HTMLDivElement, BannerProps>(({ className, variant, ...rest }, forwardedRef) => {
  const { container } = bannerStyles({ variant });

  return (
    <BannerContext.Provider value={{ variant }}>
      <div className={container({ className })} ref={forwardedRef} {...rest} />
    </BannerContext.Provider>
  );
});
Banner.displayName = "Banner";

interface BannerInlineActionProps extends React.ComponentPropsWithoutRef<"button"> {
  asChild?: boolean;
}
export const BannerInlineAction = React.forwardRef<React.ElementRef<"button">, BannerInlineActionProps>(
  ({ asChild, className, ...rest }, forwardedRef) => {
    const Component = asChild ? Slot : "button";
    const context = React.useContext(BannerContext);
    const { inlineAction } = bannerStyles({ variant: context?.variant });

    return <Component {...rest} className={inlineAction({ className })} ref={forwardedRef} />;
  },
);
BannerInlineAction.displayName = "BannerInlineAction";

interface BannerCloseActionProps extends React.ComponentPropsWithoutRef<"button"> {
  asChild?: boolean;
}
export const BannerCloseAction = React.forwardRef<React.ElementRef<"button">, BannerCloseActionProps>(
  ({ asChild, className, children, ...rest }, forwardedRef) => {
    const Component = asChild ? Slot : "button";
    const context = React.useContext(BannerContext);
    const { closeAction } = bannerStyles({ variant: context?.variant });
    const ariaLabel = rest["aria-label"] ?? "Close banner";

    return (
      <Component {...rest} aria-label={ariaLabel} className={closeAction({ className })} ref={forwardedRef}>
        <Slottable>{children}</Slottable>
        <Icon aria-hidden symbol="close-line" />
      </Component>
    );
  },
);
BannerCloseAction.displayName = "BannerCloseAction";

export const bannerStyles = tv({
  slots: {
    container: "relative font-normal text-sm w-full h-auto min-h-[44px] flex items-center gap-2 justify-center px-4",
    inlineAction: "font-medium p-0 m-0 underline underline-offset-2 [text-decoration-skip-ink:auto;] transition-colors",
    closeAction:
      "size-6 [&_svg]:size-5 transition-colors absolute right-4 grid place-items-center text-inherit inset-y-center rounded-sm bg-transparent",
  },
  variants: {
    variant: {
      info: {
        container: "bg-ds-blue-3 text-ds-blue-11 border-y border-ds-blue-7",
        inlineAction: "text-ds-blue-12 hover:decoration-inherit hover:text-inherit",
      },
      primary: {
        container: "bg-ds-teal-3 text-ds-accent-primary-11 border-y border-ds-accent-primary-7",
        inlineAction: "text-ds-accent-primary-12 hover:decoration-inherit hover:text-inherit",
      },
      warning: {
        container: "bg-ds-yellow-3 text-ds-yellow-11 border-y border-ds-yellow-7",
        inlineAction: "text-ds-yellow-12 hover:decoration-inherit hover:text-inherit",
      },
      error: {
        container: "bg-ds-red-3 text-ds-red-11 border-y border-ds-red-7",
        inlineAction: "text-ds-red-12 hover:decoration-inherit hover:text-inherit",
      },
    },
  },
  defaultVariants: {
    variant: "primary",
  },
});

const BannerContext = React.createContext<VariantProps<typeof bannerStyles> | null>(null);
